import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import swal from "sweetalert";
import * as FaIcons from "react-icons/fa";
import Title from "../GenericComponents/Title";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import "../../styles/tailwind.css";
import ProviderCategoryModal from "./ProviderCategoryModal";
import {
  ProviderSettingsInlineToolbar,
  ProviderSettingsSearchInput,
  ProviderSettingsButtonSecondary,
  ProviderSettingsAddButton,
  ProviderSettingsToolbarActions,
} from "./ProviderSettingsUi";

const PROVIDER_CATEGORY_URL = "provider-categories";

function extractCategoryList(res) {
  const payload = res?.data;
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.categories)) return payload.categories;
  return [];
}

function getCategoryName(category) {
  return category?.name || category?.title || "";
}

function getCategoryDescription(category) {
  return category?.description || category?.detail || category?.details || "";
}

function isInactiveStatus(category) {
  if (category?.active_status !== undefined) {
    return category.active_status === false || category.active_status === 0;
  }
  return category?.status === "inactive" || category?.status === 0;
}

function getProvidersCount(category) {
  return (
    category?.providers_count ??
    category?.providersCount ??
    category?.provider_count ??
    0
  );
}

function ProviderCategories({ embedded = false }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", status: "active" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await apiUrl.get(PROVIDER_CATEGORY_URL);
      setCategories(extractCategoryList(res));
    } catch (error) {
      console.error("Failed to load provider categories", error);
      swal(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load provider categories.",
        "error",
      );
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = useMemo(() => {
    const keyword = appliedSearch.trim().toLowerCase();
    if (!keyword) return categories;
    return categories.filter((category) => {
      const name = getCategoryName(category).toLowerCase();
      const description = getCategoryDescription(category).toLowerCase();
      return name.includes(keyword) || description.includes(keyword);
    });
  }, [categories, appliedSearch]);

  const handleSearchSubmit = (event) => {
    event?.preventDefault?.();
    setAppliedSearch(searchInput);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setAppliedSearch("");
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ name: "", description: "", status: "active" });
    setErrors({});
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingId(category?.id);
    setForm({
      name: getCategoryName(category),
      description: getCategoryDescription(category),
      status: isInactiveStatus(category) ? "inactive" : "active",
    });
    setErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingId(null);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const saveCategory = async (modalForm) => {
    const nextErrors = {};
    if (!modalForm.name.trim()) {
      nextErrors.name = "Category name is required.";
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setForm(modalForm);

    const payload = {
      name: modalForm.name.trim(),
      description: modalForm.description.trim(),
      active_status: modalForm.status === "active",
    };

    setSaving(true);
    try {
      if (editingId) {
        await apiUrl.put(`${PROVIDER_CATEGORY_URL}/${editingId}`, payload);
        swal("Success!", "Provider category updated successfully.", "success");
      } else {
        await apiUrl.post(PROVIDER_CATEGORY_URL, payload);
        swal("Success!", "Provider category created successfully.", "success");
      }
      setShowModal(false);
      setEditingId(null);
      await fetchCategories();
    } catch (error) {
      console.error("Failed to save provider category", error);
      swal(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to save provider category.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (category) => {
    const name = getCategoryName(category) || "this category";
    swal({
      title: "Are you sure?",
      text: `You will not be able to recover (${name})`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;
      try {
        await apiUrl.delete(`${PROVIDER_CATEGORY_URL}/${category?.id}`);
        swal(`Category (${name}) deleted successfully`, { icon: "success" });
        await fetchCategories();
      } catch (error) {
        console.error("Failed to delete provider category", error);
        swal(
          "Error",
          error?.response?.data?.message ||
            error?.message ||
            "Unable to delete provider category.",
          "error",
        );
      }
    });
  };

  const content = (
      <div className={embedded ? "" : "px-2 px-lg-4 pb-5"}>
        <ProviderSettingsInlineToolbar onSubmit={handleSearchSubmit}>
          <ProviderSettingsSearchInput
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search categories..."
          />
          <ProviderSettingsToolbarActions>
            <ProviderSettingsButtonSecondary type="submit" icon={FaIcons.FaSearch}>
              Search
            </ProviderSettingsButtonSecondary>
            <ProviderSettingsButtonSecondary onClick={handleSearchClear}>
              Clear
            </ProviderSettingsButtonSecondary>
            <ProviderSettingsAddButton onClick={openCreateModal}>
              Add Category
            </ProviderSettingsAddButton>
          </ProviderSettingsToolbarActions>
        </ProviderSettingsInlineToolbar>

        {/* Table card */}
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="w-16 px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    ID
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Category Name
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Description
                  </th>
                  <th className="w-32 px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Providers
                  </th>
                  <th className="w-28 px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                  <th className="w-36 px-4 py-3.5 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                    Created At
                  </th>
                  <th className="w-28 px-4 py-3.5 text-right text-xs font-bold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, rowIndex) => (
                    <tr key={rowIndex} className="border-t border-gray-100">
                      {[...Array(7)].map((__, cellIndex) => (
                        <td key={cellIndex} className="px-4 py-4">
                          <div
                            className="h-4 animate-pulse rounded bg-gray-200"
                            style={{ width: cellIndex === 2 ? "80%" : "55%" }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredCategories.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
                        <FaIcons.FaFolderOpen size={40} className="text-gray-300" />
                        <h6 className="mt-3 mb-1 font-semibold text-gray-600">
                          No categories found
                        </h6>
                        <p className="mb-0 text-sm text-gray-400">
                          {appliedSearch
                            ? "Try a different search keyword."
                            : "Get started by adding your first provider category."}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredCategories.map((category, index) => (
                    <tr
                      key={category?.id || index}
                      className="border-t border-gray-100 transition hover:bg-blue-50/40"
                    >
                      <td className="px-4 py-3.5 text-sm font-medium text-gray-700">
                        {category?.id ?? "—"}
                      </td>
                      <td className="px-4 py-3.5 text-sm font-semibold text-gray-900">
                        {getCategoryName(category) || "—"}
                      </td>
                      <td className="max-w-xs px-4 py-3.5 text-sm text-gray-500">
                        {getCategoryDescription(category) || "—"}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                          <FaIcons.FaBuilding size={11} />
                          {getProvidersCount(category)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                            isInactiveStatus(category)
                              ? "bg-gray-100 text-gray-500"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {isInactiveStatus(category) ? "Inactive" : "Active"}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600">
                        {category?.created_at
                          ? moment(category.created_at).format("DD MMM YYYY")
                          : "—"}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => openEditModal(category)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-0 bg-blue-50 text-blue-600 transition hover:bg-blue-600 hover:text-white"
                          >
                            <FaIcons.FaEdit size={13} />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => handleDelete(category)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border-0 bg-red-50 text-red-500 transition hover:bg-red-500 hover:text-white"
                          >
                            <FaIcons.FaTrashAlt size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );

  return (
    <>
      {!embedded ? <Title title="Provider Categories" back={true} /> : null}
      {content}

      <ProviderCategoryModal
        show={showModal}
        isEditing={Boolean(editingId)}
        initialForm={form}
        saving={saving}
        errors={errors}
        onClose={closeModal}
        onFieldChange={handleFieldChange}
        onSubmit={saveCategory}
      />
    </>
  );
}

export default ProviderCategories;
